import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import multiplayerService from '../services/multiplayerService';
import Input from '../components/ui/Input';
import { Home, DoorOpen, ArrowLeft } from 'lucide-react';
import './MultiplayerPage.css';

const MultiplayerPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState(null); // 'create' or 'join'
  const [joinCode, setJoinCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Room settings for create
  const [settings, setSettings] = useState({
    max_players: 10,
    total_rounds: 5,
    round_time_limit: 60,
  });

  const handleCreateRoom = async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await multiplayerService.createRoom(settings);
      if (response.success) {
        navigate(`/multiplayer/${response.data.room_code}`);
      } else {
        setError(response.message || 'Failed to create room');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create room');
    } finally {
      setLoading(false);
    }
  };

  const handleJoinRoom = async (e) => {
    e.preventDefault();
    if (!joinCode.trim()) {
      setError('Please enter a room code');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const response = await multiplayerService.joinRoom(joinCode.trim());
      if (response.success) {
        navigate(`/multiplayer/${response.data.room_code}`);
      } else {
        setError(response.message || 'Failed to join room');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Room not found or game already started');
    } finally {
      setLoading(false);
    }
  };

  const renderModeSelection = () => (
    <div className="mode-selection">
      <h1 className="page-title">Multiplayer</h1>
      <p className="page-subtitle">Challenge friends and compete in real-time.</p>

      <div className="mode-cards">
        <div className="mode-option-card" onClick={() => setMode('create')}>
          <div className="mode-option-icon"><Home size={22} /></div>
          <h3>Create Room</h3>
          <p>Start a new game and invite friends with a code</p>
          <button className="cta-primary">Create Room</button>
        </div>

        <div className="mode-option-card" onClick={() => setMode('join')}>
          <div className="mode-option-icon"><DoorOpen size={22} /></div>
          <h3>Join Room</h3>
          <p>Enter a room code to jump into an existing game</p>
          <button className="cta-secondary">Join Room</button>
        </div>
      </div>
    </div>
  );

  const renderCreateRoom = () => (
    <div className="create-room">
      <button className="back-button" onClick={() => setMode(null)}>
        <ArrowLeft size={16} /> Back
      </button>
      
      <h1 className="page-title">Create Room</h1>
      <p className="page-subtitle">Set up your game room</p>
      
      <div className="settings-card">
        <div className="setting-group">
          <label>Max Players</label>
          <Input
            type="number"
            min="2"
            max="20"
            value={settings.max_players}
            onChange={(e) => setSettings({ ...settings, max_players: parseInt(e.target.value) || 2 })}
          />
          <span className="setting-hint">2-20 players</span>
        </div>
        
        <div className="setting-group">
          <label>Number of Rounds</label>
          <Input
            type="number"
            min="1"
            max="10"
            value={settings.total_rounds}
            onChange={(e) => setSettings({ ...settings, total_rounds: parseInt(e.target.value) || 5 })}
          />
          <span className="setting-hint">1-10 rounds</span>
        </div>
        
        <div className="setting-group">
          <label>Time per Round (seconds)</label>
          <Input
            type="number"
            min="30"
            max="300"
            value={settings.round_time_limit}
            onChange={(e) => setSettings({ ...settings, round_time_limit: parseInt(e.target.value) || 60 })}
          />
          <span className="setting-hint">30-300 seconds</span>
        </div>
        
        {error && <div className="error-message">{error}</div>}
        
        <button
          className="cta-primary create-button"
          onClick={handleCreateRoom}
          disabled={loading}
        >
          {loading ? 'Creating...' : 'Create Room'}
        </button>
      </div>
    </div>
  );

  const renderJoinRoom = () => (
    <div className="join-room">
      <button className="back-button" onClick={() => setMode(null)}>
        <ArrowLeft size={16} /> Back
      </button>
      
      <h1 className="page-title">Join Room</h1>
      <p className="page-subtitle">Enter the 6-character room code</p>
      
      <div className="join-card">
        <form onSubmit={handleJoinRoom}>
          <div className="code-input-wrapper">
            <Input
              type="text"
              placeholder="ABCDEF"
              value={joinCode}
              onChange={(e) => setJoinCode(e.target.value.toUpperCase().slice(0, 6))}
              maxLength={6}
              className="code-input"
              autoFocus
            />
          </div>
          
          {error && <div className="error-message">{error}</div>}
          
          <button
            type="submit"
            className="cta-primary join-button"
            disabled={loading || joinCode.length !== 6}
          >
            {loading ? 'Joining...' : 'Join Room'}
          </button>
        </form>
      </div>
    </div>
  );

  return (
    <div className="multiplayer-page">
      <div className="container">
        {mode === null && renderModeSelection()}
        {mode === 'create' && renderCreateRoom()}
        {mode === 'join' && renderJoinRoom()}
      </div>
    </div>
  );
};

export default MultiplayerPage;
